import { addPlaceholderToEmptySuperscript } from 'zycommon-web/utils/symbolic-tools/common-number';
import { getSharedKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/shared-keyboard-layouts';

export const TEXT_MODE_COMMANDS = ['\\text', '\\mathrm'];
export const PRIMARY_TEXT_MODE_COMMAND = '\\mathrm';
const textModeAlt = '(?:\\\\text|\\\\mathrm)';

/**
 * Whether a MathLive text atom command is a supported text-mode wrapper.
 * @param {string|undefined} command MathLive atom command (e.g. `\text`, `\mathrm`).
 * @returns {boolean}
 */
export function isTextModeCommand(command) {
    return TEXT_MODE_COMMANDS.includes(command);
}

/**
 * Build a regex matching `\text{…}` or `\mathrm{…}` with a given inner pattern.
 * @param {string} innerPattern Regex pattern for content inside the braces.
 * @param {string} [flags] Regex flags (default `u`).
 * @returns {RegExp}
 */
export function buildTextModeWrapperRegex(innerPattern, flags = 'u') {
    return new RegExp(`${textModeAlt}\\{${innerPattern}\\}`, flags);
}

export const emptyTextPlaceholderRegex = buildTextModeWrapperRegex('\\\\placeholder\\{\\}');
export const emptyTextPlaceholderSuffix = `${PRIMARY_TEXT_MODE_COMMAND}{\\placeholder{}}`;
export const emptyTextPlaceholderSuffixes = TEXT_MODE_COMMANDS.map(command => `${command}{\\placeholder{}}`);
export const textWithTrailingPlainCaptureRegex = new RegExp(`${textModeAlt}\\{([^}\\\\]*)\\}([a-zA-Z0-9_,\\s-]+)$`, 'u');
export const textWithTrailingEmptyPlaceholderCaptureRegex = new RegExp(
    `${textModeAlt}\\{\\\\placeholder\\{\\}\\}([a-zA-Z0-9_,\\s-]+)$`,
    'u',
);

// Clear editor if ONLY an empty super/subscript remains
const clearOnlyEmptySuperOrSubscript = {
    isScenarioPresent: value => /^[\^_]\{\}$/u.test(value.trim()),
    handleScenario: editor => {
        editor.executeCommand('moveToMathfieldStart');
        editor.executeCommand('deleteForward');
    },
};

// Deleting _'s value adds a placeholder
const addPlaceholderToEmptySubscript = {
    isScenarioPresent: value => value.includes('_{}'),
    handleScenario: editor => editor.executeCommand(['insert', '\\placeholder{}']),
};

// When a superscript is added after a subscript, apply the superscript to the base variable instead of the subscript.
const superscriptAfterSubscriptRegex = /_\{(.+)\^\{\\placeholder\{\}\}\}/u;
const fixSuperscriptAfterSubscript = {
    isScenarioPresent: value => superscriptAfterSubscriptRegex.test(value),
    handleScenario: editor => {
        const match = editor.value.match(superscriptAfterSubscriptRegex);
        if (match) {
            // The regex captures strings like "_{content^{\placeholder{}}}"
            // We need to transform them to "_{content}^{\placeholder{}}"
            const content = match[1]; // First capture group contains the content
            const replacement = `_{${content}}^{\\placeholder{}}`;
            editor.value = editor.value.replace(match[0], replacement);
        }
    },
};

// If the value after toggle is the same as the previous answer with a \mathrm{} wrapper, unwrap it.
const singleTextModeWrapRegex = new RegExp(`^${textModeAlt}\\{([^}]+)\\}$`, 'u');
const unwrapTextModeOnToggle = {
    isScenarioPresent: (value, previousUserAnswer = '') => singleTextModeWrapRegex.test(previousUserAnswer) && value === previousUserAnswer,
    handleScenario: editor => {
        const inner = editor.value.match(singleTextModeWrapRegex)[1];
        if (inner) {
            editor.value = inner;
        }
    },
};

// Surrounding text-mode wrappers with the same wrapper deletes them to toggle text mode
const doubleTextRegex = buildTextModeWrapperRegex(`${textModeAlt}\\{(.*?)\\}`);
const removeDoubleText = {
    isScenarioPresent: value => {
        return doubleTextRegex.test(value);
    },
    handleScenario: editor => {
        const match = editor.value.match(doubleTextRegex);
        if (match) {
            const innerContent = match[1];
            editor.value = editor.value.replace(doubleTextRegex, innerContent);
        }
    },
};

// Mouse Toggle Text on subscript content can include the leading _. MathLive may produce:
//   F\mathrm{\_{grav}}   — wrapper wraps \_{grav} (mouse selection; grav stays braced inside wrapper)
//   F_{\mathrm{_grav}}   — underscore inside subscript braces (alternate selection path)
const textToggleBrokenSubscriptPatterns = [
    {
        name: 'text-escaped-underscore-braced',
        regex: buildTextModeWrapperRegex('\\\\_\\{([^}]*)\\}', 'gu'),
        replacement: `_{${PRIMARY_TEXT_MODE_COMMAND}{$1}}`,
    },
    {
        name: 'text-leading-underscore-braced',
        regex: buildTextModeWrapperRegex('_\\{([^}]*)\\}', 'gu'),
        replacement: `_{${PRIMARY_TEXT_MODE_COMMAND}{$1}}`,
    },
    {
        name: 'text-escaped-underscore',
        regex: buildTextModeWrapperRegex('\\\\_([^}]*)', 'gu'),
        replacement: `_{${PRIMARY_TEXT_MODE_COMMAND}{$1}}`,
    },
    {
        name: 'text-leading-underscore',
        regex: buildTextModeWrapperRegex('_([^}]*)', 'gu'),
        replacement: `_{${PRIMARY_TEXT_MODE_COMMAND}{$1}}`,
    },
    {
        name: 'subscript-text-underscore',
        regex: /_\{(?:\\text|\\mathrm)\{_([^}]*)\}\}/gu,
        replacement: `_{${PRIMARY_TEXT_MODE_COMMAND}{$1}}`,
    },
];

const fixTextToggleSubscriptUnderscore = {
    isScenarioPresent: value =>
        textToggleBrokenSubscriptPatterns.some(({ regex }) => {
            regex.lastIndex = 0;
            return regex.test(value);
        }),
    handleScenario: editor => {
        let value = editor.value;

        for (const { regex, replacement } of textToggleBrokenSubscriptPatterns) {
            regex.lastIndex = 0;
            value = value.replace(regex, replacement);
        }

        editor.value = value;
    },
};

const plainTextContentRegex = /^[a-zA-Z][a-zA-Z0-9_,\s-]*$/u;

/**
 * Count how many empty text-mode placeholders appear in a LaTeX string.
 * @param {string} value Mathfield LaTeX value.
 * @returns {number} Number of empty text placeholders.
 */
function countEmptyTextPlaceholders(value) {
    if (!value) {
        return 0;
    }

    emptyTextPlaceholderRegex.lastIndex = 0;

    return (value.match(emptyTextPlaceholderRegex) || []).length;
}

/**
 * Whether a MathLive placeholder atom sits inside a `\text{}` or `\mathrm{}` group.
 * @param {Object|null|undefined} atom MathLive model atom.
 * @returns {boolean}
 */
function isPlaceholderInTextGroup(atom) {
    if (!atom || atom.type !== 'placeholder') {
        return false;
    }

    let ancestor = atom.parent;

    while (ancestor) {
        if (ancestor.type === 'text' && isTextModeCommand(ancestor.command)) {
            return true;
        }

        ancestor = ancestor.parent;
    }

    return false;
}

/**
 * Return the placeholder child of a text-mode atom, if one exists.
 * @param {Object|null|undefined} atom MathLive text atom.
 * @returns {Object|null} The placeholder atom, or null.
 */
function getPlaceholderInTextGroup(atom) {
    if (!atom || atom.type !== 'text' || !isTextModeCommand(atom.command)) {
        return null;
    }

    const body = atom.branch?.('body') ?? atom.body;

    if (!body) {
        return null;
    }

    const bodyAtoms = Array.isArray(body) ? body : [body];

    return bodyAtoms.find(child => child?.type === 'placeholder') ?? null;
}

/**
 * Find the model offset of the empty text placeholder nearest to a caret position.
 * @param {Object} model MathLive `_mathfield.model`.
 * @param {number} nearestToPosition Caret offset used to pick among multiple candidates.
 * @returns {number|null} Model offset of the chosen placeholder, or null if none exist.
 */
function getEmptyTextPlaceholderOffset(model, nearestToPosition) {
    const candidates = [];

    for (let index = 0; index <= model.lastOffset; index++) {
        const atom = model.atoms[index];

        if (!isPlaceholderInTextGroup(atom)) {
            continue;
        }

        candidates.push(model.offsetOf(atom));
    }

    if (!candidates.length) {
        return null;
    }

    return candidates.reduce((nearestOffset, offset) =>
        Math.abs(offset - nearestToPosition) < Math.abs(nearestOffset - nearestToPosition) ? offset : nearestOffset,
    );
}

/**
 * Place the caret inside an empty text-mode placeholder without MathLive placeholder navigation,
 * which can fall through to move-out and focus a prior field on the page.
 * @param {Object} editor A mounted MathfieldElement.
 * @param {number} [nearestToPosition] Prefer the placeholder closest to this offset.
 * @returns {boolean} Whether the caret was repositioned.
 */
export function focusEmptyTextPlaceholder(editor, nearestToPosition = editor.position) {
    // eslint-disable-next-line no-underscore-dangle
    const model = editor._mathfield?.model;

    if (!model) {
        return false;
    }

    const offset = getEmptyTextPlaceholderOffset(model, nearestToPosition);

    if (offset === null) {
        return false;
    }

    editor.position = offset;

    return true;
}

/**
 * When MathLive selects a text-mode group, redirect into its placeholder so click/arrow re-entry works.
 * @param {Object} editor A mounted MathfieldElement.
 * @returns {boolean} Whether the caret was repositioned.
 */
export function redirectEmptyTextPlaceholderSelection(editor) {
    // eslint-disable-next-line no-underscore-dangle
    const model = editor._mathfield?.model;

    if (!model) {
        return false;
    }

    const atom = model.at(editor.position);
    const placeholder = getPlaceholderInTextGroup(atom);

    if (!placeholder) {
        return false;
    }

    editor.position = model.offsetOf(placeholder);

    return true;
}

// MathLive leaves the caret outside text-mode placeholders after VK insert when math precedes it.
const focusEmptyTextPlaceholderOnInsert = {
    isScenarioPresent: (value, previousUserAnswer = '') =>
        countEmptyTextPlaceholders(value) > countEmptyTextPlaceholders(previousUserAnswer),
    handleScenario: editor => {
        focusEmptyTextPlaceholder(editor);
    },
};

// MathLive drops the text-mode wrapper when typing into an empty Toggle Text placeholder after other math.
const wrapPlainTextAfterEmptyTextToggleWithPrefix = {
    isScenarioPresent: (value, previousUserAnswer = '') => {
        const matchedSuffix = emptyTextPlaceholderSuffixes.find(suffix => previousUserAnswer.endsWith(suffix));

        if (!value || !matchedSuffix) {
            return false;
        }

        const prefix = previousUserAnswer.slice(0, -matchedSuffix.length);

        if (!value.startsWith(prefix) || value.length <= prefix.length) {
            return false;
        }

        const added = value.slice(prefix.length);

        if (buildTextModeWrapperRegex('[^}]*').test(added)) {
            return false;
        }

        return plainTextContentRegex.test(added);
    },
    handleScenario: (editor, previousUserAnswer = '') => {
        const matchedSuffix = emptyTextPlaceholderSuffixes.find(suffix => previousUserAnswer.endsWith(suffix));
        const prefix = previousUserAnswer.slice(0, -matchedSuffix.length);
        const added = editor.value.slice(prefix.length);

        editor.value = `${prefix}${PRIMARY_TEXT_MODE_COMMAND}{${added}}`;
    },
};

// MathLive drops the text-mode wrapper when typing the first character into an empty Toggle Text placeholder.
const wrapPlainTextAfterEmptyTextToggle = {
    isScenarioPresent: (value, previousUserAnswer = '') => {
        if (!value || value.includes('\\')) {
            return false;
        }

        return plainTextContentRegex.test(value) && emptyTextPlaceholderRegex.test(previousUserAnswer);
    },
    handleScenario: editor => {
        editor.value = `${PRIMARY_TEXT_MODE_COMMAND}{${editor.value}}`;
    },
};

// MathLive types subsequent characters after the closing } of \mathrm{g}, producing \mathrm{g}r instead of \mathrm{gr}
const fixTextGrowingOutsideWrapper = {
    isScenarioPresent: value => textWithTrailingPlainCaptureRegex.test(value) || textWithTrailingEmptyPlaceholderCaptureRegex.test(value),
    handleScenario: editor => {
        let { value } = editor;

        value = value.replace(textWithTrailingEmptyPlaceholderCaptureRegex, `${PRIMARY_TEXT_MODE_COMMAND}{$1}`);
        value = value.replace(textWithTrailingPlainCaptureRegex, `${PRIMARY_TEXT_MODE_COMMAND}{$1$2}`);

        editor.value = value;
    },
};

// When ^ pressed and already in ^{\placeholder{}}, remove the second ^{\placeholder{}}. Same if _ pressed when in _{\placeholder{}}
const removeExtraSuperOrSubscript = {
    isScenarioPresent: value => value.includes('^{^{\\placeholder{}}}') || value.includes('_{_{\\placeholder{}}}'),
    handleScenario: editor => {
        // Delete the new ^{\placeholder{}} or _{\placeholder{}}
        editor.executeCommand('deleteBackward');

        // Add the old \placeholder{}
        editor.executeCommand(['insert', '\\placeholder{}']);
    },
};

// When ^ pressed and already in _{\placeholder{}}, replace the _ with ^. And, vice versa.
const subscriptWithSuperscriptWithPlaceholder = '_{^{\\placeholder{}}}';
const replaceExtraSuperOrSubscript = {
    isScenarioPresent: value => value.includes(subscriptWithSuperscriptWithPlaceholder) || value.includes('^{_{\\placeholder{}}}'),
    handleScenario: editor => {
        const addSuperscript = editor.value.includes(subscriptWithSuperscriptWithPlaceholder);

        // Delete the entire match
        editor.executeCommand('deleteBackward');
        editor.executeCommand('deleteBackward');

        // Replace with the newest script and a \placeholder{}
        editor.executeCommand(['insert', `${addSuperscript ? '^' : '_'}{\\placeholder{}}`]);
    },
};

const removeEmptySuperOrSubscript = ['_', '^']
    .map(script => `${script}{}`)
    .map(emptyScript => ({
        isScenarioPresent: value => value.includes(emptyScript),
        handleScenario: editor => {
            const oldPosition = editor.position;

            // Due to a bug in MathLive 0.108.2, orphaned sub/superscripts cannot be selected or deleted using the deleteAll command, only a deleteForward or deleteBackward.
            // This approach uses MathLive's internal model to find the empty subsup atom and execute deleteForward at the appropriate position.
            // eslint-disable-next-line no-underscore-dangle
            const model = editor._mathfield?.model;

            if (model) {
                // Find an empty subsup atom
                const emptySubsup = model.findAtom(atom => {
                    if (atom.type !== 'subsup') {
                        return false;
                    }

                    // Check if the superscript or subscript branch is empty
                    const sup = atom.branch('superscript');
                    const sub = atom.branch('subscript');
                    const supEmpty = !sup || sup.length === 0 || (sup.length === 1 && sup[0].type === 'first');
                    const subEmpty = !sub || sub.length === 0 || (sub.length === 1 && sub[0].type === 'first');
                    return supEmpty || subEmpty;
                });

                if (emptySubsup) {
                    const atomOffset = model.offsetOf(emptySubsup);
                    editor.position = atomOffset - 1;
                    editor.executeCommand('deleteForward');

                    // Restore position (adjusted if needed)
                    const newLastOffset = editor.lastOffset;
                    editor.position = Math.min(oldPosition, newLastOffset);
                }
            }
        },
    }));

export const numericVariablesDeleteScenarios = [
    clearOnlyEmptySuperOrSubscript,
    addPlaceholderToEmptySubscript,
    addPlaceholderToEmptySuperscript,
];

export const numericVariablesInsertScenarios = [
    focusEmptyTextPlaceholderOnInsert,
    fixSuperscriptAfterSubscript,
    unwrapTextModeOnToggle,
    removeDoubleText,
    wrapPlainTextAfterEmptyTextToggleWithPrefix,
    wrapPlainTextAfterEmptyTextToggle,
    fixTextGrowingOutsideWrapper,
    fixTextToggleSubscriptUnderscore,
    removeExtraSuperOrSubscript,
    replaceExtraSuperOrSubscript,
    ...removeEmptySuperOrSubscript,
];

/**
 * Get the keybindings for the Numeric Variables math live editor.
 * @returns {Array} The Numeric Variables keybindings.
 */
export function getNumericVariablesKeybindings() {
    const keybindings = [];

    keybindings.push({ key: '^', command: ['insert', '^{\\placeholder{}}'] }, { key: '_', command: ['insert', '_{\\placeholder{}}'] });

    return keybindings;
}

/**
 * Get the Numeric Variables virtual keyboard layout.
 * @param {Object} commonKeys The common keys used across different virtual keyboard layouts.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {Array} symbolicQuestion.virtual_keyboard_layout_name The specific virtual keyboard layout used in the question.
 * @returns {Array} The keyboard layout.
 */
export function getNumericVariablesKeyboardLayout(commonKeys, symbolicQuestion) {
    const { virtual_keyboard_layout_name } = symbolicQuestion;

    // Try to get the shared keyboard layout
    const sharedLayout = getSharedKeyboardLayout(commonKeys, virtual_keyboard_layout_name);

    // If a shared layout exists, return it
    if (sharedLayout) {
        return sharedLayout;
    }

    // Otherwise, return kitchen sink
    return ['numeric', 'symbols', 'alphabetic', 'greek'];
}
